import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Calendar, MapPin, ArrowRight, Music, Mic2, Guitar, Store, Heart, Search, Bell } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Initialize data and fetch
        await axios.post(`${API}/init-data`);
        const [eventsRes, citiesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/cities`)
        ]);
        
        // Filter upcoming events and sort by date (closest first)
        const now = new Date();
        const upcomingEvents = eventsRes.data
          .filter(event => new Date(event.event_date) >= now)
          .sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
          .slice(0, 6);
        
        setEvents(upcomingEvents);
        setCities(citiesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatEventDate = (date) => {
    try {
      return format(new Date(date), "d MMM", { locale: es }).toUpperCase();
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('/hero-bg.jpg')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-4xl">
            <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black text-white leading-none tracking-tighter mb-6">
              ENCUENTRA EL
              <br />
              <span className="text-primary">ROCK</span>
              <br />
              EN TU MAPA
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 max-w-xl mb-8 font-mono">
              Descubre los mejores bares de rock en tu ciudad o cuando viajas. ¡Somos comunidad! Conciertos, jams, DJ sets y más.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => navigate("/bars")}
                className="btn-ruido bg-primary text-white font-mono text-base px-8 py-6 border-2 border-primary"
                data-testid="hero-bars-btn"
              >
                VER BARES
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                onClick={() => navigate("/events")}
                variant="outline"
                className="btn-ruido border-2 border-zinc-600 text-white font-mono text-base px-8 py-6"
                data-testid="hero-events-btn"
              >
                VER EVENTOS
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
            <div className="flex gap-3 justify-start mt-4">
              <button 
                onClick={() => navigate("/para-bares")}
                className="text-zinc-500 hover:text-primary font-mono text-xs flex items-center gap-1 transition-colors"
                data-testid="hero-register-bar-btn"
              >
                <Store className="w-3 h-3" />
                REGISTRAR MI BAR
              </button>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Section for Rockers (Users) */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-primary/20 text-primary font-mono text-xs mb-4">
                PARA ROCKERS
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
                TU GUÍA DEL <span className="text-primary">ROCK LOCAL</span>
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Descubre conciertos, guarda tus favoritos y no te pierdas ningún evento. 
                Todo el rock de tu ciudad en un solo lugar.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Heart className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">GUARDA EVENTOS</h4>
                    <p className="text-zinc-500 text-xs">Tus favoritos siempre a mano</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">AÑADE AL CALENDARIO</h4>
                    <p className="text-zinc-500 text-xs">Recordatorios automáticos</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Search className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">FILTRA POR GÉNERO</h4>
                    <p className="text-zinc-500 text-xs">Rock, metal, blues y más</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Bell className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">ALERTAS DE ENTRADAS</h4>
                    <p className="text-zinc-500 text-xs">Aviso cuando salgan a la venta</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate("/register")}
                className="bg-primary hover:bg-red-700 text-white font-mono px-8 py-5"
                data-testid="section-rocker-btn"
              >
                ENTRA
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
              <div className="relative bg-[#0a0a0a] border border-zinc-800 rounded-lg p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Heart className="w-8 h-8 text-primary" />
                  <div>
                    <h3 className="text-white font-bold">MIS EVENTOS</h3>
                    <p className="text-zinc-500 text-xs">3 eventos guardados</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { title: "METAL NIGHT", date: "15 ABR", bar: "Nevermind" },
                    { title: "ROCK CLÁSICO", date: "22 ABR", bar: "The Black Cat" },
                    { title: "BLUES SESSION", date: "28 ABR", bar: "Jazz & Rock" }
                  ].map((event, i) => (
                    <div key={i} className="flex items-center gap-3 bg-zinc-900 p-3 border border-zinc-800">
                      <div className="bg-primary/20 text-primary font-mono text-xs px-2 py-1">
                        {event.date}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-bold">{event.title}</p>
                        <p className="text-zinc-500 text-xs">{event.bar}</p>
                      </div>
                      <Heart className="w-4 h-4 text-primary fill-primary" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section for Bars */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-primary/5 to-transparent" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 bg-primary/20 text-primary font-mono text-xs mb-4">
                PARA BARES Y SALAS
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
                PON TU BAR EN EL <span className="text-primary">MAPA ROCK</span>
              </h2>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                Llega a miles de fans que buscan conciertos cerca de casa. Publica eventos 
                y llena tu sala cada noche.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Calendar className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">PUBLICA EVENTOS</h4>
                    <p className="text-zinc-500 text-xs">Conciertos, DJs, vermuts</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Music className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">GESTIONA CALENDARIO</h4>
                    <p className="text-zinc-500 text-xs">Organiza tus eventos</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">VISIBILIDAD</h4>
                    <p className="text-zinc-500 text-xs">Aparece en el mapa local</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-primary/20 flex items-center justify-center shrink-0">
                    <Store className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="text-white font-bold text-sm">GRATIS</h4>
                    <p className="text-zinc-500 text-xs">O Backstage 29€/mes</p>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate("/para-bares")}
                className="bg-primary hover:bg-red-700 text-white font-mono px-8 py-5"
                data-testid="section-bar-btn"
              >
                REGISTRAR MI BAR
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="absolute -inset-4 bg-primary/10 blur-3xl rounded-full" />
              <div className="relative bg-[#0a0a0a] border border-zinc-800 rounded-lg p-6 shadow-2xl">
                <div className="flex items-center gap-3 mb-6">
                  <Calendar className="w-8 h-8 text-primary" />
                  <div>
                    <h3 className="text-white font-bold">CALENDARIO DE EVENTOS</h3>
                    <p className="text-zinc-500 text-xs">Gestiona tu programación</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { title: "ROCK NIGHT", date: "VIE 15", status: "publicado" },
                    { title: "DJ SESSION", date: "SAB 16", status: "publicado" },
                    { title: "METAL FEST", date: "VIE 22", status: "borrador" }
                  ].map((event, i) => (
                    <div key={i} className="flex items-center gap-3 bg-zinc-900 p-3 border border-zinc-800">
                      <div className="bg-primary/20 text-primary font-mono text-xs px-2 py-1">
                        {event.date}
                      </div>
                      <div className="flex-1">
                        <p className="text-white text-sm font-bold">{event.title}</p>
                        <p className="text-zinc-500 text-xs uppercase">{event.status}</p>
                      </div>
                      <Calendar className="w-4 h-4 text-zinc-600" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section for Bands - HIDDEN */}

      {/* Cities Section */}
      {cities.length > 0 && (
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-8 tracking-tighter">
            CIUDADES <span className="text-primary">ACTIVAS</span>
          </h2>
          <div className="flex flex-wrap gap-3">
            {cities.map((city) => (
              <Link
                key={city.city_id}
                to={`/events?city=${city.city_id}`}
                className="group"
                data-testid={`city-link-${city.city_id}`}
              >
                <div className="bg-[#111] border-2 border-zinc-800 px-6 py-3 font-mono text-sm text-zinc-300 group-hover:border-primary group-hover:text-primary transition-all">
                  <MapPin className="inline w-4 h-4 mr-2" />
                  {city.name.toUpperCase()}
                </div>
              </Link>
            ))}
            <Link
              to="/events"
              className="bg-[#111] border-2 border-dashed border-zinc-700 px-6 py-3 font-mono text-sm text-zinc-500 hover:border-primary hover:text-primary transition-all"
              data-testid="city-add-link"
            >
              + MÁS CIUDADES PRONTO
            </Link>
          </div>
        </section>
      )}

      {/* Events Grid - Bento Style */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tighter">
            PRÓXIMOS <span className="text-primary">EVENTOS</span>
          </h2>
          <Link 
            to="/events" 
            className="font-mono text-sm text-zinc-400 hover:text-primary transition-colors flex items-center gap-2"
            data-testid="see-all-events"
          >
            VER TODOS <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#111] border border-zinc-800 h-64 animate-pulse" />
            ))}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event, index) => (
              <Link
                key={event.event_id}
                to={`/events/${event.event_id}`}
                className={`group card-ruido bg-[#111] border border-zinc-800 overflow-hidden ${
                  index === 0 ? "md:col-span-2 md:row-span-2" : ""
                }`}
                data-testid={`event-card-${event.event_id}`}
              >
                <div className="relative h-full min-h-[200px]">
                  {event.image_url ? (
                    <img
                      src={event.image_url}
                      alt={event.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 to-zinc-800 flex items-center justify-center">
                      <Music className="w-16 h-16 text-zinc-700" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                  
                  <div className="relative h-full p-6 flex flex-col justify-end">
                    <div className="mb-2 flex flex-wrap gap-2">
                      <span className="inline-block bg-primary text-white text-xs font-mono px-2 py-1">
                        {formatEventDate(event.event_date)}
                      </span>
                      {event.tags?.slice(0, 2).map((tag) => (
                        <span key={tag} className="inline-block bg-zinc-800 text-zinc-300 text-xs font-mono px-2 py-1 uppercase">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h3 className={`font-black text-white mb-2 tracking-tight ${
                      index === 0 ? "text-3xl" : "text-xl"
                    }`}>
                      {event.title}
                    </h3>
                    <p className="font-mono text-sm text-zinc-400">
                      <MapPin className="inline w-3 h-3 mr-1" />
                      {event.bar_name || "Bar"}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="bg-[#111] border border-zinc-800 p-12 text-center">
            <Guitar className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">NO HAY EVENTOS AÚN</h3>
            <p className="text-zinc-500 font-mono text-sm mb-6">
              Sé el primero en publicar un evento
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Button 
                onClick={() => navigate("/para-bares")}
                className="btn-ruido bg-primary text-white font-mono"
                data-testid="no-events-register-bar"
              >
                REGISTRAR MI BAR
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-[#111] relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1473396413399-6717ef7c4093?crop=entropy&cs=srgb&fm=jpg&q=85')"
          }}
        />
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 tracking-tight">
            LA ESCENA ROCK <span className="text-primary">TE ESPERA</span>
          </h2>
          <p className="text-lg text-zinc-400 mb-8 font-mono max-w-2xl mx-auto">
            Únete a la comunidad de locales que ya están conectando en Rockmaps.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate("/events")}
              className="bg-primary hover:bg-red-700 text-white font-mono text-lg px-10 py-6"
              data-testid="cta-events-btn"
            >
              VER EVENTOS
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              onClick={() => navigate("/register")}
              variant="outline"
              className="border-zinc-600 text-white font-mono text-lg px-10 py-6"
              data-testid="cta-register-btn"
            >
              CREAR CUENTA
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8 px-4 sm:px-6 lg:px-8 safe-area-bottom">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-black tracking-tighter">
                <span className="text-white">ROCK</span><span className="text-primary">MAPS</span>
              </span>
              <span className="text-zinc-600 font-mono text-xs">© 2026</span>
            </div>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 font-mono text-sm text-zinc-500">
              <Link to="/bars" className="hover:text-primary">BARES</Link>
              <Link to="/events" className="hover:text-primary">EVENTOS</Link>
              <Link to="/para-bares" className="hover:text-primary">SOY BAR</Link>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-4 flex flex-wrap justify-center gap-4 sm:gap-6 font-mono text-xs text-zinc-600">
            <Link to="/privacidad" className="hover:text-primary">Política de Privacidad</Link>
            <Link to="/politica-cookies" className="hover:text-primary">Política de Cookies</Link>
            <Link to="/aviso-legal" className="hover:text-primary">Aviso Legal</Link>
            <Link to="/terminos" className="hover:text-primary">Términos y Condiciones</Link>
            <button 
              onClick={() => window.resetCookieConsent && window.resetCookieConsent()}
              className="hover:text-primary cursor-pointer"
            >
              Configurar Cookies
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}
